import type { PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { effectiveYExtent, includeZero, toCanvasY } from "./scale-utils";

export type BucketsRendererStyle = Readonly<{
  fillStyle: string;
  bucketCount: number;
  minWidth: number;
}>;

/** Aggregates scalar samples into fixed-width fraction bars. */
export class BucketsRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: BucketsRendererStyle = {
    fillStyle: "#2563eb",
    bucketCount: 48,
    minWidth: 1,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isScalarEntry(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isScalarEntry(entry)) return;
    if (this.style.bucketCount <= 0) return;

    const baseExtent = effectiveYExtent(entry, request);
    if (baseExtent == null) return;
    const extent = includeZero(baseExtent);
    const zeroY = toCanvasY(0, extent, target.size.height);
    const bucketRange = (request.viewRange.end - request.viewRange.start) /
      this.style.bucketCount;
    if (bucketRange <= 0) return;

    const numerators = new Array<number>(this.style.bucketCount).fill(0);
    const denominators = new Array<number>(this.style.bucketCount).fill(0);

    for (const [x, value] of entry.series.range(
      request.viewRange.start,
      request.viewRange.end,
    ).points()) {
      const bucket = Math.floor((x - request.viewRange.start) / bucketRange);
      if (bucket < 0 || bucket >= this.style.bucketCount) continue;
      if (value > 0) numerators[bucket]++;
      denominators[bucket]++;
    }

    target.ctx.fillStyle = this.style.fillStyle;
    const bucketWidth = Math.max(
      this.style.minWidth,
      target.size.width / this.style.bucketCount,
    );

    for (let i = 0; i < this.style.bucketCount; i++) {
      const denominator = denominators[i]!;
      if (denominator === 0) continue;
      const fraction = numerators[i]! / denominator;
      const x = (i / this.style.bucketCount) * target.size.width;
      const y = toCanvasY(fraction, extent, target.size.height);
      const top = Math.min(y, zeroY);
      const height = Math.abs(y - zeroY);
      target.ctx.fillRect(x, top, bucketWidth, height);
    }
  }
}
